import React from "react";
import Button from "../Button";
import classes from "./record.module.scss";

/** True for values a read view should not show: null, "", whitespace, an empty list. */
export const isEmptyValue = (value: React.ReactNode): boolean =>
    value === null ||
    value === undefined ||
    value === false ||
    (typeof value === "string" && value.trim() === "") ||
    (Array.isArray(value) && value.filter((item) => !isEmptyValue(item)).length === 0);

export interface ReadFieldProps {
    label: string;
    value?: React.ReactNode;
    /** Hover help for the label. */
    tooltip?: string;
    /** Spans the whole grid row (notes, addresses). */
    wide?: boolean;
    /** Show the field even when empty (as "—"). Off by default: empties fold into NotRecorded. */
    showEmpty?: boolean;
}

/** One label + value in a read view. Renders nothing for an empty value unless `showEmpty`. */
export const ReadField: React.FC<ReadFieldProps> = ({ label, value, tooltip, wide, showEmpty }) => {
    const isEmpty = isEmptyValue(value);
    if (isEmpty && !showEmpty) return null;
    return (
        <div className={`${classes.field} ${wide ? classes.fieldWide : ""}`}>
            <span className={classes.fieldLabel} title={tooltip}>{label}</span>
            <span className={classes.fieldValue}>{isEmpty ? "—" : value}</span>
        </div>
    );
};

interface FieldGridProps {
    /** `grid` = auto-fill 200px columns, label above value. `rows` = label left, value right. */
    layout?: "grid" | "rows";
    /** Shorthand for a list of ReadFields. */
    fields?: ReadFieldProps[];
    children?: React.ReactNode;
    className?: string;
}

/** Read-view fields on a responsive grid. */
export const FieldGrid: React.FC<FieldGridProps> = ({ layout = "grid", fields, children, className }) => (
    <div className={`${layout === "rows" ? classes.fieldRows : classes.fieldGrid} ${className ?? ""}`}>
        {fields?.map((field) => <ReadField key={field.label} {...field} />)}
        {children}
    </div>
);

interface NotRecordedProps {
    /** The empty fields' labels. */
    labels: string[];
    /** Switches the page to edit mode. */
    onAdd?: () => void;
    addLabel?: string;
}

/** One quiet line for every empty field: "Not recorded: Facebook · Instagram [Add in edit]". */
export const NotRecorded: React.FC<NotRecordedProps> = ({ labels, onAdd, addLabel = "Add in edit" }) => {
    if (labels.length === 0) return null;
    return (
        <div className={classes.notRecorded}>
            <span>Not recorded: {labels.join(" · ")}</span>
            {onAdd && (
                <Button type="button" variant="ghost" size="xs" icon="pencil" title="Switch to edit mode to fill these in" onClick={onAdd}>
                    {addLabel}
                </Button>
            )}
        </div>
    );
};

/** The labels of `fields` whose value is empty — feed them to NotRecorded. */
export const emptyLabels = (fields: { label: string; value?: React.ReactNode }[]): string[] =>
    fields.filter((field) => isEmptyValue(field.value)).map((field) => field.label);

export default FieldGrid;
